/// <reference lib="dom" />
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { OutstandingPoGroup, User, Invoice, Item, HhInvoice, HhInvoiceItem, OutstandingDataResponse, PurchaseOrder, PurchaseOrderItem } from 'shared/types.js';
import { api } from './api.js';
import { formatDate, currencyFormatter } from './utils.js';
import { InvoiceDetail, HhInvoiceDetail, PurchaseOrderDetail } from './common.js';

const formatViewedDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
};

const isCreditPurchase = (purchase: PurchaseOrder): boolean =>
    (purchase.type || "").trim().toLowerCase() === "credit p.o.";

const formatPurchaseTotal = (purchase: PurchaseOrder): string => {
    const amount = Number(purchase.total_cost) || 0;
    if (!isCreditPurchase(purchase)) return currencyFormatter.format(amount);
    return `-${currencyFormatter.format(Math.abs(amount))}`;
};

const HhInvoiceComparisonModal = ({
    initialHhInvoice,
    relatedInvoices,
    relatedPurchases,
    user,
    onClose,
    onHhInvoiceUpdated,
    onItemStatusChange,
    onClearAllItems,
    onClearInvoice,
    onClearWithIssues,
    onUpdateNotes,
    onHhSaveChanges,
    onInvoiceSaveChanges,
    onSelectLinkedDocInModal,
}: {
    initialHhInvoice: HhInvoice;
    relatedInvoices: Invoice[];
    relatedPurchases: PurchaseOrder[];
    user: User;
    onClose: (updatedInvoice: HhInvoice) => void;
    onHhInvoiceUpdated: (updatedInvoice: HhInvoice) => void;
    onItemStatusChange: (itemId: number, isCleared: boolean) => Promise<HhInvoice | null>;
    onClearAllItems: (invoice: HhInvoice) => Promise<HhInvoice | null>;
    onClearInvoice: (invoice: HhInvoice) => Promise<HhInvoice | null>;
    onClearWithIssues: (invoice: HhInvoice) => Promise<HhInvoice | null>;
    onUpdateNotes: (invoice: HhInvoice, notes: string) => Promise<HhInvoice | null>;
    onHhSaveChanges: (invoiceNo: string, items: HhInvoiceItem[], invoiceData: HhInvoice) => Promise<HhInvoice | null>;
    onInvoiceSaveChanges: (invoice: Invoice) => Promise<void>;
    onSelectLinkedDocInModal: (source: 'invoices' | 'save_orders', docNo: string) => void;
}) => {
    const [hhInvoice, setHhInvoice] = useState<HhInvoice>(initialHhInvoice);
    const [hhInvoiceItems, setHhInvoiceItems] = useState<HhInvoiceItem[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [invoiceItems, setInvoiceItems] = useState<Item[]>([]);
    const [selectedPurchase, setSelectedPurchase] = useState<PurchaseOrder | null>(null);
    const [purchaseItems, setPurchaseItems] = useState<PurchaseOrderItem[]>([]);
    const [popoutPane, setPopoutPane] = useState<'left' | 'right' | null>(null);
    const [popoutContainer, setPopoutContainer] = useState<HTMLElement | null>(null);
    const [isLoadingHh, setIsLoadingHh] = useState(true);
    const [isLoadingInv, setIsLoadingInv] = useState(false);
    const [isLoadingPurchase, setIsLoadingPurchase] = useState(false);
    const isMounted = useRef(true);
    const popoutWindowRef = useRef<Window | null>(null);
    useEffect(() => {
        const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
        if (count === 0) {
            document.body.classList.add('modal-open');
        }
        document.body.dataset.modalOpenCount = String(count + 1);
        return () => {
            const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
            const newCount = Math.max(0, count - 1);
            if (newCount === 0) {
                document.body.classList.remove('modal-open');
            }
            document.body.dataset.modalOpenCount = String(newCount);
        };
    }, []);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; }
    }, []);
    useEffect(() => {
        const fetchHhData = async () => {
            setIsLoadingHh(true);
            try {
                await api.patch(`/api/hh-invoices/${initialHhInvoice.invoice_no}/mark-as-viewed`);
                const [fullInvoice, items] = await Promise.all([
                    api.get<HhInvoice>(`/api/hh-invoices/${initialHhInvoice.invoice_no}`),
                    api.get<HhInvoiceItem[]>(`/api/hh-invoices/${initialHhInvoice.invoice_no}/items`)
                ]);
                if (isMounted.current) {
                    setHhInvoice(fullInvoice);
                    setHhInvoiceItems(items);
                    onHhInvoiceUpdated(fullInvoice);
                }
            } catch (err) {
                // No-op
            } finally {
                if (isMounted.current) setIsLoadingHh(false);
            }
        };
        fetchHhData();
    }, [initialHhInvoice.invoice_no, onHhInvoiceUpdated]);
    useEffect(() => {
        const fetchInvData = async () => {
            if (!selectedInvoice) return;
            if (selectedInvoice.source === 'save_orders') {
                setInvoiceItems([]);
                return;
            }
            setIsLoadingInv(true);
            try {
                const items = await api.get<Item[]>(`/api/invoices/${selectedInvoice.id}/items`);
                if (isMounted.current) setInvoiceItems(items);
            } catch (err) {
                // No-op
            } finally {
                if (isMounted.current) setIsLoadingInv(false);
            }
        };
        fetchInvData();
    }, [selectedInvoice]);
    useEffect(() => {
        const fetchPurchaseData = async () => {
            if (!selectedPurchase) return;
            setIsLoadingPurchase(true);
            try {
                const items = await api.get<PurchaseOrderItem[]>(`/api/purchases/${selectedPurchase.id}/items`);
                if (isMounted.current) setPurchaseItems(items);
            } catch (err) {
                // No-op
            } finally {
                if (isMounted.current) setIsLoadingPurchase(false);
            }
        };
        fetchPurchaseData();
    }, [selectedPurchase]);
    const createHandler = (handler: Function, ...args: any[]) => async (...handlerArgs: any[]) => {
        const result = await handler(...args, ...handlerArgs);
        if (result && isMounted.current) {
            setHhInvoice(result);
            onHhInvoiceUpdated(result);
            const items = await api.get<HhInvoiceItem[]>(`/api/hh-invoices/${result.invoice_no}/items`);
            if(isMounted.current) setHhInvoiceItems(items);
        }
    };
    const createCloseHandler = (handler: Function, ...args: any[]) => async (...handlerArgs: any[]) => {
        const result = await handler(...args, ...handlerArgs);
        if (result) {
            onHhInvoiceUpdated(result);
            onClose(result);
        }
    };
    const closePopout = useCallback(() => {
        const popoutWindow = popoutWindowRef.current;
        popoutWindowRef.current = null;
        setPopoutPane(null);
        setPopoutContainer(null);
        if (popoutWindow && !popoutWindow.closed) {
            popoutWindow.close();
        }
    }, []);
    const openPopout = useCallback((pane: 'left' | 'right') => {
        const existingWindow = popoutWindowRef.current;
        if (existingWindow && !existingWindow.closed && popoutPane === pane) {
            existingWindow.focus();
            return;
        }
        if (existingWindow && !existingWindow.closed) {
            existingWindow.close();
        }

        const title = pane === 'left' ? `Vendor Invoice ${hhInvoice.invoice_no}` : `System Documents ${hhInvoice.invoice_no}`;
        const popoutWindow = window.open('', `hh-invoice-comparison-${pane}`, 'width=1100,height=900,resizable=yes,scrollbars=yes');
        if (!popoutWindow) {
            window.alert('The popout window was blocked by the browser.');
            return;
        }

        popoutWindow.document.open();
        popoutWindow.document.write(`<!doctype html><html><head><title>${title}</title></head><body><div id="comparison-popout-root"></div></body></html>`);
        popoutWindow.document.close();

        const base = popoutWindow.document.createElement('base');
        base.href = window.location.href;
        popoutWindow.document.head.prepend(base);
        document.querySelectorAll('link[rel="stylesheet"], style').forEach(node => {
            popoutWindow.document.head.appendChild(node.cloneNode(true));
        });

        const root = popoutWindow.document.getElementById('comparison-popout-root');
        if (!root) {
            popoutWindow.close();
            return;
        }

        popoutWindow.document.body.className = document.body.className;
        root.className = 'comparison-popout-root';
        popoutWindow.onbeforeunload = () => {
            if (popoutWindowRef.current === popoutWindow) {
                popoutWindowRef.current = null;
                if (isMounted.current) {
                    setPopoutPane(null);
                    setPopoutContainer(null);
                }
            }
        };

        popoutWindowRef.current = popoutWindow;
        setPopoutPane(pane);
        setPopoutContainer(root);
        popoutWindow.focus();
    }, [hhInvoice.invoice_no, popoutPane]);
    useEffect(() => closePopout, [closePopout]);
    const canViewCosts = user.permissions?.can_view_costs === 'true';
    const canAccessInventoryLookup = user.permissions?.can_access_inventory_lookup === 'true';
    const canAccessTracker = user.permissions?.can_access_tracker === 'true';
    const canEditInvoices = user.permissions?.can_edit_invoices === 'true';
    const vendorPaneContent = isLoadingHh ? (
        <div className="placeholder"><p>Loading Vendor Invoice...</p></div>
    ) : (
        <HhInvoiceDetail
            invoice={hhInvoice}
            items={hhInvoiceItems}
            onClear={() => onClose(hhInvoice)}
            onItemStatusChange={createHandler(onItemStatusChange)}
            onClearAllItems={createCloseHandler(onClearAllItems, hhInvoice)}
            onClearInvoice={createCloseHandler(onClearInvoice, hhInvoice)}
            onClearWithIssues={createCloseHandler(onClearWithIssues, hhInvoice)}
            onUpdateNotes={createHandler(onUpdateNotes, hhInvoice)}
            onSaveChanges={createHandler(onHhSaveChanges)}
            canEditInvoices={canEditInvoices}
        />
    );
    const systemPaneContent = selectedInvoice ? (
        isLoadingInv ? (
            <div className="placeholder"><p>Loading System Invoice...</p></div>
        ) : (
            <InvoiceDetail
                invoice={selectedInvoice}
                items={invoiceItems}
                onClear={() => setSelectedInvoice(null)}
                onSelectLinkedDoc={onSelectLinkedDocInModal}
                canViewCosts={canViewCosts}
                canAccessInventoryLookup={canAccessInventoryLookup}
                onSaveChanges={onInvoiceSaveChanges}
                canEditInvoices={canEditInvoices}
            />
        )
    ) : selectedPurchase ? (
        isLoadingPurchase ? (
            <div className="placeholder">
                <p>Loading System Purchase...</p>
            </div>
        ) : (
            <PurchaseOrderDetail
                purchase={selectedPurchase}
                items={purchaseItems}
                onClear={() => setSelectedPurchase(null)}
            />
        )
    ) : (
        <div style={{ padding: "1.5rem" }} className="comparison-pane-list">
            <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>Related System Invoices</h3>
            {relatedInvoices.length > 0 ? (
                <ul className="modal-file-list">
                    {relatedInvoices.map(inv => (
                        <li key={`${inv.source}-${inv.id}`}>
                            <button className="modal-file-item" onClick={() => setSelectedInvoice(inv)}>
                                <span>{inv.doc_no} ({inv.source === 'save_orders' ? 'SO' : 'Inv'})</span>
                                <span>{inv.customer_name}</span>
                                <span>{currencyFormatter.format(Number(inv.total))}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="placeholder"><p>No related system invoices found.</p></div>
            )}
            <h3 style={{fontSize: '1.25rem', fontWeight: 600, margin: '1rem 0'}}>Related System Purchases</h3>
            {relatedPurchases.length > 0 ? (
                <ul className="modal-file-list">
                    {relatedPurchases.map((purchase, index) => (
                        <li key={`${purchase.id}-${purchase.po}-${index}`}>
                            <button 
                                className="modal-file-item" 
                                onClick={() => {
                                    setSelectedInvoice(null);
                                    setSelectedPurchase(purchase);
                                }}
                            >
                                <span>{purchase.po}</span>
                                <span>{purchase.vendor}</span>
                                <span style={isCreditPurchase(purchase) ? { color: "var(--credit-color)" } : undefined}>
                                    {formatPurchaseTotal(purchase)}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="placeholder">
                    <p>
                        No related system purchases found.
                    </p>
                </div>
            )}
        </div>
    );
    const popoutContent = popoutContainer && popoutPane ? createPortal(
        <div className="comparison-popout-shell">
            <header className="modal-header">
                <strong>{popoutPane === 'left' ? 'Vendor Invoice' : 'System Documents'}: {hhInvoice.invoice_no}</strong>
                <button onClick={closePopout}>Return to split</button>
            </header>
            <div className={`comparison-pane ${popoutPane === 'left' ? 'comparison-pane-left' : 'comparison-pane-right'}`}>
                {popoutPane === 'left' ? vendorPaneContent : systemPaneContent}
            </div>
        </div>,
        popoutContainer
    ) : null;
    return (
        <div className="modal-backdrop" onClick={() => onClose(hhInvoice)}>
            <div className="modal modal-comparison-view" onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <strong id="hh-invoice-modal-title">HH Invoice Comparison: {hhInvoice.invoice_no}</strong>
                    <div className="comparison-layout-controls" aria-label="Comparison layout controls">
                        <button
                            className={popoutPane === 'left' ? 'active' : ''}
                            onClick={() => openPopout('left')}
                            title="Popout Home Invoice"
                        >
                            Popout Left
                        </button>
                        <button
                            className={popoutPane === null ? 'active' : ''}
                            onClick={closePopout}
                            title="Return to Split View"
                        >
                            Return to Split
                        </button>
                        <button
                            className={popoutPane === 'right' ? 'active' : ''}
                            onClick={() => openPopout('right')}
                            title="Popout System Invoice"
                        >
                            Popout Right
                        </button>
                    </div>
                    <button onClick={() => onClose(hhInvoice)} aria-label="Close modal">&times;</button>
                </header>
                <div className={`modal-body-split comparison-popout-${popoutPane || 'none'}`}>
                    {popoutPane !== 'left' && (
                        <div className="comparison-pane comparison-pane-left">
                            {vendorPaneContent}
                        </div>
                    )}
                    {popoutPane !== 'right' && (
                        <div className="comparison-pane comparison-pane-right">
                            {systemPaneContent}
                        </div>
                    )}
                </div>
                {popoutContent}
            </div>
        </div>
    );
};

const getClerkBadgeInitials = (clerk: string | null | undefined): string => {
    const trimmed = clerk?.trim();
    if (!trimmed) return '';
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
        return parts.map(part => part[0]).join('').toUpperCase();
    }
    return trimmed.toUpperCase();
};

const OutstandingInvoiceItem = React.memo(({
    inv,
    onViewDetails,
    onAssignClerk,
    clerks,
}: {
    inv: HhInvoice,
    onViewDetails: (invoice: HhInvoice) => void,
    onAssignClerk?: (invoice: HhInvoice, clerk: string) => void,
    clerks?: string[],
}) => {
    const isCreditInvoice = (Number(inv.amount) || 0) < 0 || (Number(inv.vendor_amount) || 0) < 0;
    const badgeClerk = inv.assigned_clerk?.trim() || inv.clerk_group?.trim() || '';
    const clerkBadgeInitials = getClerkBadgeInitials(badgeClerk);
    const clerkBadgeTitle = inv.assigned_clerk?.trim()
        ? `Manually assigned to ${badgeClerk}`
        : `Clerk group ${badgeClerk}`;
    return (
        <li key={inv.invoice_no}>
            <div className="outstanding-item-info">
                <div className="outstanding-item-row">
                    <div className="outstanding-invoice-no-container">
                        <button
                            className="clickable-link"
                            onClick={() => onViewDetails(inv)}
                        >
                            {inv.invoice_no}
                        </button>
                        {(() => {
                            if (inv.status && inv.status !== 'new') {
                                if ((inv.status === 'fully_cleared' || inv.status === 'partially_cleared' || inv.status === 'cleared_with_issues') && inv.cleared_by_user_name && inv.cleared_at) {
                                    const clearedDate = new Date(inv.cleared_at).toLocaleDateString('en-CA', {month: 'short', day: 'numeric' });
                                    const statusText = inv.status === 'fully_cleared' ? 'Cleared' : inv.status === 'partially_cleared' ? 'Partially Cleared' : 'Issues';
                                    const statusClass = `status-${inv.status.replace(/_/g, '-')}`;
                                    return (
                                        <span className={`outstanding-status-badge ${statusClass}`}>
                                            {statusText} ({inv.cleared_by_user_name.split(' ')[0]}{' '} - {clearedDate})
                                        </span>
                                    );
                                }
                                return (
                                    <span className={`outstanding-status-badge status-${inv.status.replace(/_/g, '-')}`}>
                                        {inv.status.replace(/_/g, ' ')}
                                    </span>
                                );
                            }
                            if (inv.is_viewed_by_user) {
                                return (
                                    <span className="outstanding-status-badge status-viewed">
                                        Viewed{' '} {inv.user_viewed_date ? `(${formatViewedDate(inv.user_viewed_date)})` : ''}
                                    </span>
                                );
                            }
                            if (inv.status === 'new') {
                                return <span className="outstanding-status-badge status-new">New</span>;
                            }
                            return null;
                        })()}
                        {inv.notes && inv.notes_updated_at && (!inv.user_viewed_date || new Date(inv.notes_updated_at) > new Date(inv.user_viewed_date)) && (
                            <span className="outstanding-status-badge status-note-added">
                                Note Added ({inv.notes_updated_by_user_name ? inv.notes_updated_by_user_name.split(' ')[0] : 'Unknown'}{' '}-{' '}{formatViewedDate(inv.notes_updated_at)})</span>
                        )}
                        {clerkBadgeInitials && (
                            <span className="outstanding-clerk-group-badge" title={clerkBadgeTitle}>
                                {clerkBadgeInitials}
                            </span>
                        )}
                    </div>
                    <span className="outstanding-item-amount" style={isCreditInvoice ? { color: 'var(--credit-color)' } : undefined}>
                        {inv.amount != null
                            ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(inv.amount)
                            : ''
                        }
                    </span>
                </div>

                <div className="outstanding-item-row">
                    <span className="outstanding-item-vendor">{inv.vendor}</span>
                    <span className="outstanding-item-date">{formatDate(inv.invoice_date)}</span>
                </div>
            </div>
            {onAssignClerk && clerks && (
                <div className="outstanding-item-actions">
                    <select
                        className="assign-clerk-select"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onAssignClerk(inv, e.currentTarget.value)}
                        value=""
                        aria-label={`Assign invoice ${inv.invoice_no} to a clerk`}
                    >
                        <option value="" disabled>Assign to...</option>
                        {clerks.map(clerk => (
                            <option key={clerk} value={clerk}>{clerk}</option>
                        ))}
                    </select>
                </div>
            )}
        </li>
    );
});

const PaginationControls = ({ page, totalCount, limit, onPageChange }: { page: number, totalCount: number, limit: number, onPageChange: (newPage: number) => void }) => {
    const totalPages = Math.ceil(totalCount / limit);
    const [jumpToPage, setJumpToPage] = useState(String(page));
    useEffect(() => {
        setJumpToPage(String(page));
    }, [page]);
    const handleJump = (e: React.FormEvent) => {
        e.preventDefault();
        const pageNum = parseInt(jumpToPage, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
            onPageChange(pageNum);
        } else {
            setJumpToPage(String(page));
        }
    };
    if (totalPages <= 1) return null;
    return (
        <div className="pagination-controls tracker-pagination">
            <span>{totalCount} total groups</span>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button onClick={() => onPageChange(1)} disabled={page <= 1} title="First page">&laquo; First</button>
                    <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}>Previous</button>
                </div>
                <form onSubmit={handleJump} className="page-jump-form">
                    <span>Page</span>
                    <input
                        type="number"
                        value={jumpToPage}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJumpToPage(e.currentTarget.value)}
                        onBlur={() => { if(jumpToPage === '') setJumpToPage(String(page)) }}
                        min="1"
                        max={totalPages}
                        className="page-jump-input"
                        aria-label="Jump to page"
                    />
                    <span> of {totalPages}</span>
                </form>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>Next</button>
                    <button onClick={() => onPageChange(totalPages)} disabled={page >= totalPages} title="Last page">Last &raquo;</button>
                </div>
            </div>
        </div>
    );
};

export const OutstandingTracker = ({ user }: { user: User }) => {
    const statusOptions = [
        { value: 'new', label: 'New' },
        { value: 'partially_cleared', label: 'Partially Cleared' },
        { value: 'cleared_with_issues', label: 'Cleared With Issues' },
        { value: 'fully_cleared', label: 'Cleared' },
    ] as const;
    const defaultStatuses = new Set<string>(['new', 'viewed', 'partially_cleared']);
    const [groups, setGroups] = useState<OutstandingPoGroup[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [clerkPoRanges, setClerkPoRanges] = useState<Record<string, number[]>>({});
    
    const [modalInvoice, setModalInvoice] = useState<Invoice | null>(null);
    const [modalInvoiceItems, setModalInvoiceItems] = useState<Item[]>([]);
    const [modalPurchase, setModalPurchase] = useState<PurchaseOrder | null>(null);
    const [modalPurchaseItems, setModalPurchaseItems] = useState<PurchaseOrderItem[]>([]);
    const [comparisonData, setComparisonData] = useState<{ hhInvoice: HhInvoice, relatedInvoices: Invoice[], relatedPurchases: PurchaseOrder[] } | null>(null);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(defaultStatuses));
    const [hideEmptyRelated, setHideEmptyRelated] = useState(false);
    const [hideNullPo, setHideNullPo] = useState(true);
    const [creditsOnly, setCreditsOnly] = useState(false);
    const [hideSystemSaveOrders, setHideSystemSaveOrders] = useState(false);
    const [hideSystemInvoices, setHideSystemInvoices] = useState(false);
    const [hideSystemPurchases, setHideSystemPurchases] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [assigningInvoiceToPo, setAssigningInvoiceToPo] = useState<string | null>(null);
    const [docNoToAssign, setDocNoToAssign] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [assigningPurchaseToPo, setAssigningPurchaseToPo] = useState<string | null>(null);
    const [purchasePoToAssign, setPurchasePoToAssign] = useState('');
    const [isAssigningPurchase, setIsAssigningPurchase] = useState(false);
    const [editingPoNumber, setEditingPoNumber] = useState<string | null>(null);
    const [editedPoNumber, setEditedPoNumber] = useState('');
    const [isUpdatingPoNumber, setIsUpdatingPoNumber] = useState(false);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
    const statusMenuRef = useRef<HTMLDivElement | null>(null);
    const moreFiltersRef = useRef<HTMLDivElement | null>(null);

    const allClerks = useMemo(() => Object.keys(clerkPoRanges).sort(), [clerkPoRanges]);
    
    const allowedClerks = useMemo(() => {
        if (user.permissions && 'allowed_clerks' in user.permissions) {
            const clerks = user.permissions.allowed_clerks;
            return Array.isArray(clerks) ? clerks : [];
        }
        return null;
    }, [user.permissions]);

    const getDefaultClerk = () => {
        const defaultClerk = user.permissions?.default_clerk;
        if (defaultClerk === 'Unknown' && user.permissions?.can_view_unknown_clerk !== 'true') {
            return 'All';
        }
        if (defaultClerk && (allowedClerks === null || allowedClerks.includes(defaultClerk))) {
            return defaultClerk;
        }
        return 'All';
    };

    const [selectedClerk, setSelectedClerk] = useState<string>(getDefaultClerk);

    const clerksForFilter = useMemo(() => {
        if (allowedClerks !== null) {
            return [...allowedClerks].sort();
        }
        return Object.keys(clerkPoRanges).sort();
    }, [clerkPoRanges, allowedClerks]);
    useEffect(() => {
        if (selectedClerk !== 'All' && selectedClerk !== 'Unknown' && !clerksForFilter.includes(selectedClerk)) {
            setSelectedClerk('All');
        }
    }, [clerksForFilter, selectedClerk]);
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) return;
            if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
                setIsStatusMenuOpen(false);
            }
            if (moreFiltersRef.current && !moreFiltersRef.current.contains(event.target)) {
                setIsMoreFiltersOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);
    useEffect(() => {
        setPage(1);
    }, [selectedClerk, selectedStatuses, appliedSearchQuery, hideEmptyRelated, hideNullPo, creditsOnly, hideSystemSaveOrders, hideSystemInvoices, hideSystemPurchases]);
    useEffect(() => {
        api.get<Record<string, number[]>>('/api/tracker-clerk-po-ranges')
            .then(setClerkPoRanges)
            .catch(() => {});
    }, []);
    useEffect(() => {
        if (modalInvoice || modalPurchase) {
            const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
            if (count === 0) {
                document.body.classList.add('modal-open');
            }
            document.body.dataset.modalOpenCount = String(count + 1);
            return () => {
                const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
                const newCount = Math.max(0, count - 1);
                if (newCount === 0) {
                    document.body.classList.remove('modal-open');
                }
                document.body.dataset.modalOpenCount = String(newCount);
            };
        }
    }, [modalInvoice, modalPurchase]);
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                clerk: selectedClerk,
                search: appliedSearchQuery,
                page: String(page),
                limit: String(limit),
            });
            if (hideEmptyRelated) {
                params.append('hideEmptyRelated', 'true');
            }
            if (hideNullPo) {
                params.append('hideNullPo', 'true');
            }
            if (creditsOnly) {
                params.append('creditsOnly', 'true');
            }
            if (hideSystemSaveOrders) {
                params.append('hideSystemSaveOrders', 'true');
            }
            if (hideSystemInvoices) {
                params.append('hideSystemInvoices', 'true');
            }
            if (hideSystemPurchases) {
                params.append('hideSystemPurchases', 'true');
            }
            selectedStatuses.forEach(status => params.append('statuses', status));
            const outstandingData = await api.get<OutstandingDataResponse>(`/api/outstanding-invoices?${params.toString()}`);
            setGroups(outstandingData.groups);
            setTotalCount(outstandingData.totalCount);
        } catch (error) {
            setGroups([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [page, limit, selectedClerk, selectedStatuses, appliedSearchQuery, hideEmptyRelated, hideNullPo, creditsOnly, hideSystemSaveOrders, hideSystemInvoices, hideSystemPurchases]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    const handleInvoiceClick = async (invoice: Invoice) => {
        setModalPurchase(null);
        setModalPurchaseItems([]);
        setModalInvoice(invoice);
        setIsModalLoading(true);
        try {
            if (invoice.source === 'save_orders') {
                setModalInvoiceItems([]);
                setIsModalLoading(false);
                return;
            }
            const items = await api.get<Item[]>(`/api/invoices/${invoice.id}/items`);
            setModalInvoiceItems(items);
        } catch (err) {
            setModalInvoiceItems([]);
        } finally {
            setIsModalLoading(false);
        }
    };
    const handleCloseComparisonModal = (finalInvoiceState: HhInvoice) => {
        let shouldKeep = false;
        if (finalInvoiceState.status === null) {
            if (selectedStatuses.has('viewed')) shouldKeep = true;
        } else if (finalInvoiceState.status) {
            if (selectedStatuses.has(finalInvoiceState.status)) shouldKeep = true;
        }
        if (!shouldKeep || finalInvoiceState.nonTracked) {
            setGroups(prev => prev.map(g => ({
                ...g,
                outstandingInvoices: g.outstandingInvoices.filter(i => i.invoice_no !== finalInvoiceState.invoice_no)
            })).filter(g => g.outstandingInvoices.length > 0));
        }
        setComparisonData(null);
    };
    const handleHhInvoiceUpdated = useCallback((updatedInvoice: HhInvoice) => {
        setGroups(prevGroups => prevGroups.map(group => {
            const index = group.outstandingInvoices.findIndex(i => i.invoice_no === updatedInvoice.invoice_no);
            if (index === -1) return group;
            const newOutstanding = [...group.outstandingInvoices];
            newOutstanding[index] = updatedInvoice;
            return { ...group, outstandingInvoices: newOutstanding };
        }));
    }, []);
    const handleHhInvoiceClick = (invoice: HhInvoice) => {
        const group = groups.find(g => g.outstandingInvoices.some(inv => inv.invoice_no === invoice.invoice_no));
        if (group) {
            setComparisonData({ hhInvoice: invoice, relatedInvoices: group.relatedInvoices, relatedPurchases: group.relatedPurchases });
        }
    };
    const handlePurchaseClick = async (purchase: PurchaseOrder) => {
        setModalInvoice(null);
        setModalInvoiceItems([]);
        setModalPurchase(purchase);
        setIsModalLoading(true);
        try {
            const items = await api.get<PurchaseOrderItem[]>(`/api/purchases/${purchase.id}/items`);
            setModalPurchaseItems(items);
        } catch (err) {
            setModalPurchaseItems([]);
        } finally {
            setIsModalLoading(false);
        }
    };
    const handleItemStatusChange = async (itemId: number, isCleared: boolean): Promise<HhInvoice | null> => {
        if (!comparisonData) return null;
        try {
            await api.patch(`/api/hh-invoice-items/${itemId}/toggle-cleared`, { isCleared });
            const updatedInvoice = await api.get<HhInvoice>(`/api/hh-invoices/${comparisonData.hhInvoice.invoice_no}`);
            return updatedInvoice;
        } catch (error) {
            window.alert('Failed to update item status.');
            return null;
        }
    };
    const handleUpdateNotes = async (invoice: HhInvoice, notes: string) => {
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/notes`, { notes });
            const updatedInvoice = await api.get<HhInvoice>(`/api/hh-invoices/${invoice.invoice_no}`);
            return updatedInvoice;
        } catch (error) {
            throw error;
        }
    };
    const handleHhSaveChanges = async (invoiceNo: string, updatedItems: HhInvoiceItem[], invoiceData: HhInvoice) => {
        try {
            await api.put(`/api/hh-invoices/${invoiceNo}/items`, { items: updatedItems, invoice: invoiceData });
            const updatedInvoice = await api.get<HhInvoice>(`/api/hh-invoices/${invoiceNo}`);
            if (comparisonData?.hhInvoice.po_no !== updatedInvoice.po_no) {
                fetchData();
            }
            return updatedInvoice;
        } catch (error) {
            throw error;
        }
    };
    const handleClearAllItems = async (invoice: HhInvoice) => {
        try {
            const updatedInvoice = await api.patch<HhInvoice>(`/api/hh-invoices/${invoice.invoice_no}/clear-all-items`);
            return updatedInvoice;
        } catch (error) {
            window.alert('Failed to clear all items.');
            return null;
        }
    };
    const handleClearInvoice = async (invoice: HhInvoice) => {
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/status`, { status: 'fully_cleared' });
            return api.get<HhInvoice>(`/api/hh-invoices/${invoice.invoice_no}`);
        } catch (error) {
            window.alert("Failed to clear invoice.");
            return null;
        }
    };
    const handleClearWithIssues = async (invoice: HhInvoice) => {
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/status`, { status: 'cleared_with_issues' });
            return api.get<HhInvoice>(`/api/hh-invoices/${invoice.invoice_no}`);
        } catch (error) {
            window.alert('Failed to update invoice status.');
            return null;
        }
    };
	const handleCloseModal = () => {
        setModalInvoice(null);
        setModalInvoiceItems([]);
        setModalPurchase(null);
        setModalPurchaseItems([]);
    };
    const handleAssignClerk = async (invoice: HhInvoice, clerk: string) => {
        if (!clerk) return;
        if (!window.confirm(`Are you sure you want to assign invoice ${invoice.invoice_no} to clerk ${clerk}?`)) {
            return;
        }
        try {
            setIsLoading(true);
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/assign-clerk`, { clerk });
            await fetchData(); 
        } catch (error) {
            window.alert(`Failed to assign clerk: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setIsLoading(false);
        }
    };
    const handleModalSaveChanges = async (updatedInvoice: Invoice) => {
        await api.patch(`/api/document/${updatedInvoice.source}/${updatedInvoice.doc_no}/po-number`, { po_no: updatedInvoice.po_no });
        if (selectedClerk === 'All') {
            fetchData();
        }
    };
    const handleSelectLinkedDocInModal = async (source: 'invoices' | 'save_orders', docNo: string) => {
        try {
            const document = await api.get<Invoice>(`/api/document/by-doc-no?source=${source}&doc_no=${docNo}`);
            if (document) {
                handleInvoiceClick(document);
            } else {
                window.alert('Linked document not found.');
            }
        } catch (error) {
            window.alert('Error fetching linked document.');
        }
    };
    const handleStatusChange = (status: string) => {
        setSelectedStatuses(prev => {
            const newSet = new Set(prev);
            if (status === 'new') {
                if (newSet.has('new')) {
                    newSet.delete('new');
                    newSet.delete('viewed');
                } else {
                    newSet.add('new');
                    newSet.add('viewed');
                }
            } else {
                if (newSet.has(status)) {
                    newSet.delete(status);
                } else {
                    newSet.add(status);
                }
            }
            return newSet;
        });
    };
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setAppliedSearchQuery(searchQuery.trim());
    };
    const selectedStatusLabels = statusOptions
        .filter(status => selectedStatuses.has(status.value))
        .map(status => status.label);
    const activeAdvancedFiltersCount = (hideEmptyRelated ? 1 : 0) + (hideNullPo ? 1 : 0) + (creditsOnly ? 1 : 0) + (hideSystemSaveOrders ? 1 : 0) + (hideSystemInvoices ? 1 : 0) + (hideSystemPurchases ? 1 : 0);
    const handleResetFilters = () => {
        setSelectedClerk(getDefaultClerk());
        setSelectedStatuses(new Set(defaultStatuses));
        setHideEmptyRelated(false);
        setHideNullPo(true);
        setCreditsOnly(false);
        setHideSystemSaveOrders(false);
        setHideSystemInvoices(false);
        setHideSystemPurchases(false);
        setSearchQuery('');
        setAppliedSearchQuery('');
    };
    
    const handleAssignInvoice = async (poNumber: string) => {
        const normalizedDocNoToAssign = docNoToAssign.trim();
        if (!normalizedDocNoToAssign) {
            window.alert('Please enter an invoice document number.');
            return;
        }
        setIsAssigning(true);
        try {
            await api.post('/api/outstanding-invoices/assign-to-po', {
                docNo: normalizedDocNoToAssign,
                poToAssign: poNumber,
            });
            window.alert(`Invoice ${normalizedDocNoToAssign} assigned to PO group ${poNumber}.`);
            setDocNoToAssign('');
            setAssigningInvoiceToPo(null);
            fetchData();
        } catch (error) {
            window.alert(`Failed to assign invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleAssignPurchase = async (poNumber: string) => {
        const normalizedPurchasePoToAssign = purchasePoToAssign.trim();
        if (!normalizedPurchasePoToAssign) {
            window.alert('Please enter a system purchase PO number.');
            return;
        }
        setIsAssigningPurchase(true);
        try {
            await api.post('/api/outstanding-invoices/assign-purchase-to-po', {
                purchasePo: normalizedPurchasePoToAssign,
                poToAssign: poNumber,
            });
            window.alert(`Purchase ${normalizedPurchasePoToAssign} assigned to PO group ${poNumber}.`);
            setPurchasePoToAssign('');
            setAssigningPurchaseToPo(null);
            fetchData();
        } catch (error) {
            window.alert(`Failed to assign purchase: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsAssigningPurchase(false);
        }
    };

    const startEditingPoNumber = (poNumber: string) => {
        setEditingPoNumber(poNumber);
        setEditedPoNumber(poNumber === 'None' ? '' : poNumber);
    };

    const handleUpdatePoNumber = async (currentPoNo: string) => {
        const normalizedNewPo = editedPoNumber.trim();
        if (!normalizedNewPo || normalizedNewPo.toLowerCase() === 'none') {
            window.alert('Please enter a valid PO number.');
            return;
        }
        if (normalizedNewPo === currentPoNo) {
            setEditingPoNumber(null);
            setEditedPoNumber('');
            return;
        }
        if (!window.confirm(`Are you sure you want to change the PO for all HH invoices in group ${currentPoNo} to ${normalizedNewPo}?`)) {
            return;
        }
        setIsUpdatingPoNumber(true);
        try {
            await api.patch('/api/outstanding-invoices/po-number', {
                currentPoNo,
                newPoNo: normalizedNewPo,
            });
            setEditingPoNumber(null);
            setEditedPoNumber('');
            await fetchData();
        } catch (error) {
            window.alert(`Failed to update PO number: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsUpdatingPoNumber(false);
        }
    };
    
    const canViewCosts = user.permissions?.can_view_costs === 'true';
    const canAccessInventoryLookup = user.permissions?.can_access_inventory_lookup === 'true';
    const canAccessTracker = user.permissions?.can_access_tracker === 'true';
    const canManageClerkPo = user.permissions?.can_access_settings === 'true';
    const canEditInvoices = user.permissions?.can_edit_invoices === 'true';

    return (
        <div className="tracker-view-wrapper">
            <div className="tracker-filter-bar">
                <div className="tracker-filter-controls">
                    <div className="tracker-filter-group">
                        <label htmlFor="clerk-filter">Clerk</label>
                        <select id="clerk-filter" value={selectedClerk} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedClerk(e.currentTarget.value)} disabled={isLoading}>
                            <option value="All">All</option>
                            {clerksForFilter.map(clerk => <option key={clerk} value={clerk}>{clerk}</option>)}
                            {user.permissions?.can_view_unknown_clerk === 'true' && <option value="Unknown">?</option>}
                        </select>
                    </div>

                    <div className="tracker-filter-group filter-popover" ref={statusMenuRef}>
                        <label>Status</label>
                        <button
                            type="button"
                            className="filter-popover-trigger"
                            onClick={() => {
                                setIsStatusMenuOpen(open => !open);
                                setIsMoreFiltersOpen(false);
                            }}
                            disabled={isLoading}
                        >
                            <span>{selectedStatusLabels.length > 0 ? `${selectedStatusLabels.length} selected` : 'Select statuses'}</span>
                            <span className="filter-caret">{isStatusMenuOpen ? '▴' : '▾'}</span>
                        </button>
                        {isStatusMenuOpen && (
                            <div className="filter-popover-menu">
                                {statusOptions.map(status => (
                                    <label key={status.value} className="filter-menu-option">
                                        <input
                                            type="checkbox"
                                            checked={selectedStatuses.has(status.value)}
                                            onChange={() => handleStatusChange(status.value)}
                                            disabled={isLoading}
                                        />
                                        <span>{status.label}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="tracker-filter-group filter-popover" ref={moreFiltersRef}>
                        <label>More</label>
                        <button
                            type="button"
                            className="filter-popover-trigger"
                            onClick={() => {
                                setIsMoreFiltersOpen(open => !open);
                                setIsStatusMenuOpen(false);
                            }}
                            disabled={isLoading}
                        >
                            <span>Filters</span>
                            {activeAdvancedFiltersCount > 0 && <span className="filter-pill-count">{activeAdvancedFiltersCount}</span>}
                            <span className="filter-caret">{isMoreFiltersOpen ? '▴' : '▾'}</span>
                        </button>
                        {isMoreFiltersOpen && (
                            <div className="filter-popover-menu">
                                <label className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={hideEmptyRelated}
                                        onChange={(e) => setHideEmptyRelated(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span>Hide groups with no related system records</span>
                                </label>
                                <label className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={hideNullPo}
                                        onChange={(e) => setHideNullPo(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span>Hide PO = NULL/None</span>
                                </label>
                                <label className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={creditsOnly}
                                        onChange={(e) => setCreditsOnly(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span>Credits only</span>
                                </label>
                                <label className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={hideSystemSaveOrders}
                                        onChange={(e) => setHideSystemSaveOrders(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span>Hide System Save Orders</span>
                                </label>
                                <label className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={hideSystemInvoices}
                                        onChange={(e) => setHideSystemInvoices(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span>Hide System Invoices</span>
                                </label>
                                <label className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={hideSystemPurchases}
                                        onChange={(e) => setHideSystemPurchases(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                    <span>Hide System Purchases</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="tracker-filter-group">
                        <button
                            type="button"
                            className="tracker-reset-filters-btn"
                            onClick={handleResetFilters}
                            disabled={isLoading}
                        >
                            Reset
                        </button>
                    </div>
                </div>

                <form className="tracker-search-bar" onSubmit={handleSearchSubmit}>
                    <svg xmlns="http://www.w.org/2000/svg" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path></svg>
                    <input
                        type="search"
                        placeholder="Search by Invoice #, PO, Vendor..."
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)}
                        aria-label="Search HH invoices"
                        disabled={isLoading}
                    />
                    <button type="submit" className="tracker-search-button" disabled={isLoading}>Search</button>
                </form>
            </div>
            {isLoading ? <div className="tracker-message">Loading HH Invoices...</div> : (
                <>
                    {groups.length > 0 ? (
                        <div className="tracker-container">
                            {groups.map(group => (
                                <div key={group.poNumber} className="po-card">
                                    <h2 className="po-card-header po-card-header-editable">
                                        {editingPoNumber === group.poNumber ? (
                                            <form
                                                className="po-card-header-edit-form"
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    handleUpdatePoNumber(group.poNumber);
                                                }}
                                            >
                                                <span>PO #</span>
                                                <input
                                                    type="text"
                                                    value={editedPoNumber}
                                                    onChange={(e) => setEditedPoNumber(e.currentTarget.value)}
                                                    disabled={isUpdatingPoNumber}
                                                    autoFocus
                                                />
                                                <button type="submit" disabled={isUpdatingPoNumber}>
                                                    {isUpdatingPoNumber ? 'Saving...' : 'Save'}
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isUpdatingPoNumber}
                                                    onClick={() => {
                                                        setEditingPoNumber(null);
                                                        setEditedPoNumber('');
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </form>
                                        ) : (
                                            <>
                                                <span>PO # {group.poNumber}</span>
                                                {canManageClerkPo && (
                                                    <button
                                                        type="button"
                                                        className="po-card-header-edit-button"
                                                        onClick={() => startEditingPoNumber(group.poNumber)}
                                                        title={`Edit PO number ${group.poNumber}`}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </h2>
                                    <div className="po-card-content">
                                        <section className="tracker-section">
                                            <h3>Outstanding ({group.outstandingInvoices.length})</h3>
                                            <ul className="outstanding-list">
                                                {group.outstandingInvoices.map(inv => (
                                                    <OutstandingInvoiceItem
                                                        key={inv.invoice_no}
                                                        inv={inv}
                                                        onViewDetails={handleHhInvoiceClick}
                                                        onAssignClerk={handleAssignClerk}
                                                        clerks={allClerks}
                                                    />
                                                ))}
                                            </ul>
                                        </section>
                                        <section className="tracker-section">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>
                                                <h3 style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0 }}>Related System Invoices ({group.relatedInvoices.length})</h3>
                                                {user.permissions?.can_access_tracker === 'true' && group.poNumber !== 'None' && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button 
                                                            className="btn-secondary" 
                                                            style={{padding: '0.25rem 0.5rem', fontSize: '0.8rem'}}
                                                            onClick={() => {
                                                                setAssigningInvoiceToPo(group.poNumber === assigningInvoiceToPo ? null : group.poNumber);
                                                                setAssigningPurchaseToPo(null);
                                                            }}
                                                        >
                                                            {assigningInvoiceToPo === group.poNumber ? 'Cancel' : '+ Assign Invoice'}
                                                        </button>
                                                        <button 
                                                            className="btn-secondary" 
                                                            style={{padding: '0.25rem 0.5rem', fontSize: '0.8rem'}}
                                                            onClick={() => {
                                                                setAssigningPurchaseToPo(group.poNumber === assigningPurchaseToPo ? null : group.poNumber);
                                                                setAssigningInvoiceToPo(null);
                                                            }}
                                                        >
                                                            {assigningPurchaseToPo === group.poNumber ? 'Cancel' : '+ Assign Purchase'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {assigningInvoiceToPo === group.poNumber && (
                                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Enter Invoice Doc No."
                                                        value={docNoToAssign}
                                                        onChange={(e) => setDocNoToAssign(e.target.value)}
                                                        style={{ flexGrow: 1, padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)' }}
                                                        autoFocus
                                                    />
                                                    <button 
                                                        className="btn-primary" 
                                                        style={{padding: '0.5rem 1rem'}}
                                                        onClick={() => handleAssignInvoice(group.poNumber)}
                                                        disabled={isAssigning}
                                                    >
                                                        {isAssigning ? 'Saving...' : 'Save'}
                                                    </button>
                                                </div>
                                            )}

                                            {assigningPurchaseToPo === group.poNumber && (
                                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Enter the 5-digit Epicor-generated PO #"
                                                        value={purchasePoToAssign}
                                                        onChange={(e) => setPurchasePoToAssign(e.target.value)}
                                                        style={{ flexGrow: 1, padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)' }}
                                                        autoFocus
                                                    />
                                                    <button 
                                                        className="btn-primary" 
                                                        style={{padding: '0.5rem 1rem'}}
                                                        onClick={() => handleAssignPurchase(group.poNumber)}
                                                        disabled={isAssigningPurchase}
                                                    >
                                                        {isAssigningPurchase ? 'Saving...' : 'Save'}
                                                    </button>
                                                </div>
                                            )}

                                            {group.relatedInvoices.length > 0 ? (
                                                <table className="related-invoices-table">
                                                    <thead>
                                                        <tr><th>Date</th><th>Doc #</th><th>Customer</th><th className="text-right">Total</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {group.relatedInvoices.map(inv => (
                                                            <tr key={`${inv.source}-${inv.id}`} onClick={() => handleInvoiceClick(inv)} className={inv.status === 'invoiced' ? 'is-invoiced' : ''}>
                                                                <td>{formatDate(inv.date)}</td>
                                                                <td>{inv.doc_no} {' '} {inv.source === 'save_orders' ? '(SO)' : ''}</td>
                                                                <td>{inv.customer_name}</td>
                                                                <td className={`text-right ${inv.status === 'invoiced' ? 'status-invoiced' : ''}`}>
                                                                {inv.status === 'invoiced' ? 'INVOICED' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(inv.total))}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : <p>No related system invoices found.</p>}

                                            <h3 style={{ marginTop: "1rem" }}>
                                                Related System Purchases (
                                                {group.relatedPurchases.length})
                                            </h3>
                                            {group.relatedPurchases.length >
                                            0 ? (
                                                <table className="related-invoices-table">
                                                    <thead>
                                                        <tr><th>Date</th><th>PO #</th><th>Vendor</th><th>Ref</th><th>Clerk</th><th className="text-right">Total</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {group.relatedPurchases.map((purchase, index) => (
                                                            <tr key={`${purchase.id}-${purchase.po}-${index}`} onClick={() => handlePurchaseClick(purchase)} >
                                                                <td>{formatDate(purchase.date)}</td>
                                                                <td>{purchase.po}</td>
                                                                <td>{purchase.vendor}</td>
                                                                <td>{purchase.ref || ""}</td>
                                                                <td>{purchase.clerk || ""}</td>
                                                                <td className="text-right" style={isCreditPurchase(purchase) ? {color: "var(--credit-color)"} : undefined}>{formatPurchaseTotal(purchase)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : <p>No related system purchases found.</p>}
                                        </section>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : <div className="tracker-message">No HH invoices for the selected criteria.</div>}
                    <PaginationControls
                        page={page}
                        totalCount={totalCount}
                        limit={limit}
                        onPageChange={setPage}
                    />
                </>
            )}
            {(modalInvoice || modalPurchase) && (
                <div className="modal-backdrop" onClick={handleCloseModal} role="dialog" aria-modal="true" aria-labelledby="invoice-modal-title">
                    <div className="modal modal-invoice-detail" onClick={e => e.stopPropagation()}>
                        <header className="modal-header">
                            <strong id="invoice-modal-title">{modalInvoice ? `Invoice Detail: ${modalInvoice.doc_no}` : `Purchase Detail:${modalPurchase?.po || ""}`}</strong>
                            <button onClick={handleCloseModal} aria-label="Close modal">&times;</button>
                        </header>
                        <div className="modal-body">
                            {isModalLoading ? (
                                <div className="placeholder"><p>Loading invoice details...</p></div>
                            ) : modalInvoice ? (
                                <InvoiceDetail 
                                    invoice={modalInvoice} 
                                    items={modalInvoiceItems} 
                                    onClear={handleCloseModal} 
                                    onSelectLinkedDoc={handleSelectLinkedDocInModal}
                                    canViewCosts={canViewCosts}
                                    canAccessInventoryLookup={canAccessInventoryLookup}
                                    onSaveChanges={handleModalSaveChanges}
                                    canEditInvoices={canEditInvoices}
                                />
                            ) : modalPurchase ? (
                                <PurchaseOrderDetail
                                    purchase={modalPurchase}
                                    items={modalPurchaseItems}
                                    onClear={handleCloseModal}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
            {comparisonData && (
                 <HhInvoiceComparisonModal
                    initialHhInvoice={comparisonData.hhInvoice}
                    relatedInvoices={comparisonData.relatedInvoices}
                    relatedPurchases={comparisonData.relatedPurchases}
                    user={user}
                    onClose={handleCloseComparisonModal}
                    onHhInvoiceUpdated={handleHhInvoiceUpdated}
                    onItemStatusChange={handleItemStatusChange}
                    onClearAllItems={handleClearAllItems}
                    onClearInvoice={handleClearInvoice}
                    onClearWithIssues={handleClearWithIssues}
                    onUpdateNotes={handleUpdateNotes}
                    onHhSaveChanges={handleHhSaveChanges}
                    onInvoiceSaveChanges={handleModalSaveChanges}
                    onSelectLinkedDocInModal={handleSelectLinkedDocInModal}
                 />
            )}
        </div>
    );
};
